// ── SettingsDeploymentPage ─────────────────────────────────────────────────
//
// CFGIA.A2 (#1537) — tenant-level Deployment config editor. Wraps the shared
// Step2DeploymentPolicy form (also used by the onboarding wizard) on a
// standalone settings surface so admins can edit deployment environments, the
// CI pipeline, and release controls after onboarding. Backend CRUD shipped with
// the wizard (GET /tenants/:tenantId/deployment-policy — any member; POST —
// TENANT_ADMIN-gated + audited); this page is the missing post-onboarding UI.
//
// Surfaced for ALL tenants: the deployment-policy endpoint is mode-agnostic
// (the wizard only collects it in the BYOC flow, but a Managed tenant can still
// own a deployment policy).
//
// LANE NOTE (CFG-IA): the <Route> (App.tsx) and the Settings-index entry card
// (SettingsPage.tsx) are intentionally NOT added here. They are the only files
// A2 shares with A1/A3/A4; they append into the CFG-IA anchors that A1 (#1536)
// single-sources, so they are deferred to a clean post-A1-merge follow-up to
// avoid an N² conflict on the anchor block. Until then this page is built and
// tested but unreachable from the Settings index.

import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Step2DeploymentPolicy from '../components/wizard/steps/Step2DeploymentPolicy';
import { HelpPopover } from '../components/help';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: 20,
  marginBottom: 16,
};

export default function SettingsDeploymentPage() {
  const { myTenant, loading } = useMyTenant();
  const tenantId = myTenant?.tenantId;
  const isAdmin = isAdminRole(myTenant?.role);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <Sidebar />
      <main style={{ flex: 1, padding: 32, maxWidth: 760 }}>
        <div style={{ marginBottom: 16 }}>
          <Link
            to="/settings"
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'var(--text-secondary)',
              textDecoration: 'none',
            }}
          >
            ← Back to settings
          </Link>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 700,
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Deployment
          </h1>
          <HelpPopover
            id="deployment-settings"
            variant="info"
            heading="Deployment"
            body={
              <>
                <p style={{ margin: '0 0 8px' }}>
                  Define your deployment environments, the CI pipeline, and the release controls
                  BluePrint applies across your organisation. The Deployment Agent reads this policy
                  when it provisions and promotes builds.
                </p>
                <p style={{ margin: 0 }}>
                  Changes apply tenant-wide. Only tenant admins can edit.
                </p>
              </>
            }
          />
        </div>
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            color: 'var(--text-secondary)',
            marginBottom: 24,
          }}
        >
          Configure the deployment environments, CI provider, branch strategy, and release gates
          that govern how BluePrint ships your application.
        </p>

        {loading || !tenantId ? (
          <div style={{ ...cardStyle, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>
            Loading…
          </div>
        ) : (
          <div style={cardStyle}>
            <Step2DeploymentPolicy
              tenantId={tenantId}
              canEdit={isAdmin}
              showIntro={false}
              submitLabel="Save changes"
            />
          </div>
        )}
      </main>
    </div>
  );
}
